import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Scan, Check, AlertTriangle, RefreshCw, Loader2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';
import { DeliveryPoSummary } from '@/components/po/DeliveryPoSummary';

/**
 * Generic Bundle Receipt Entry Component for any section.
 *
 * PROGRESSIVE RECEIPT (2026-06): every scan auto-commits to the backend
 * immediately (POST /internal-transfer/receive-bundle) so the bundle is
 * available for the next operation right away. The document stays
 * "Partially Received" and auto-closes when every expected bundle is in —
 * there is NO manual "Complete Receipt" step.
 *
 * Props:
 * - sectionName: The section identifier (e.g., "Inhouse", "Processing")
 * - sectionDisplayName: Display name for the section
 * - backRoute: Route to navigate back to
 */
const SectionBundleReceiptEntry = ({
  sectionName = "Inhouse",
  sectionDisplayName = "Inhouse Section",
  backRoute = "/sections/inhouse"
}) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleInput, setBundleInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);
  const bundleInputRef = useRef(null);

  const {
    drafts,
    activeDraftId,
    saving,
    lastSavedAt,
    autoSave,
    clearDraft,
    loadDraft,
    removeDraft,
    setActiveDraftId,
  } = useOperationDraft({
    moduleKey: `section:${sectionName}`,
    operationType: 'bundle_receipt',
    allowZero: true,
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Scans persist server-side per scan, so the draft is just a resumable
  // bookmark of the in-progress document.
  useEffect(() => {
    if (!selectedDocument) return;
    const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
    autoSave({
      label: `${sectionDisplayName} — ${docId} (${scannedBundles.length} received)`,
      bundleCount: scannedBundles.length,
      documentId: docId,
      documentNo: docId,
      payload: { document: selectedDocument },
    });
  }, [scannedBundles, selectedDocument, autoSave, sectionDisplayName]);

  const expectedTotalOf = (doc) =>
    doc?.bundle_count || doc?.bundles?.length || doc?.bundle_codes?.length || 0;

  const receivedRowsOf = (doc) =>
    (doc?.received_bundles || []).map((b) => ({
      bundle_barcode: typeof b === 'string' ? b.toUpperCase() : (b.bundle_barcode || '').toUpperCase(),
      scanned_at: (typeof b === 'object' && b?.scanned_at) || null,
      status: 'received',
    })).filter((r) => r.bundle_barcode);

  const applyDocument = (doc) => {
    setSelectedDocument(doc);
    setScannedBundles(receivedRowsOf(doc));
    setBundleInput('');
    setTimeout(() => bundleInputRef.current?.focus(), 100);
  };

  const handleResumeDraft = async (draftId) => {
    try {
      const d = await loadDraft(draftId);
      const doc = d.payload?.document;
      if (!doc) throw new Error('no doc');
      // Prefer a fresh copy from the incoming list so received_bundles is current.
      const docId = doc.transfer_id || doc.entry_id;
      const fresh = documents.find((x) => (x.transfer_id || x.entry_id) === docId) || doc;
      applyDocument(fresh);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  useEffect(() => {
    if (selectedDocument && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [selectedDocument]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/consignments/section/${encodeURIComponent(sectionName)}/incoming`);
      const docsForReceipt = (response.data.consignments || []).filter(c =>
        c.status === 'received' || c.status === 'accepted'
      );
      setDocuments(docsForReceipt);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents for receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = (docId) => {
    const existing = (drafts || []).find((d) => d.document_id === docId);
    if (existing) { handleResumeDraft(existing.draft_id); return; }
    const doc = documents.find(d => (d.transfer_id || d.entry_id) === docId);
    setResumedDraft(null);
    setActiveDraftId(null); // manual doc pick starts a fresh bookmark
    applyDocument(doc);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    resetToSelection();
  };

  const resetToSelection = () => {
    setSelectedDocument(null);
    setScannedBundles([]);
    setBundleInput('');
    setResumedDraft(null);
    fetchDocuments();
  };

  const handleScanBundle = async () => {
    if (!bundleInput.trim() || isScanning) return;

    const barcode = bundleInput.trim().toUpperCase();

    if (scannedBundles.some(b => b.bundle_barcode === barcode)) {
      toast.error('Bundle already received');
      setBundleInput('');
      return;
    }

    // Client-side pre-check that the bundle belongs to this document.
    const docBundles = selectedDocument.bundles || selectedDocument.bundle_codes || [];
    const bundleBarcodes = docBundles.length > 0
      ? (typeof docBundles[0] === 'string' ? docBundles : docBundles.map(b => b.bundle_barcode || b)).map(b => (b || '').toUpperCase())
      : [];
    if (bundleBarcodes.length > 0 && !bundleBarcodes.includes(barcode)) {
      toast.error(`Bundle ${barcode} is not part of this document`);
      setBundleInput('');
      return;
    }

    setIsScanning(true);
    try {
      const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
      const resp = await apiClient.post('/internal-transfer/receive-bundle', {
        transfer_id: docId,
        section_name: sectionName,
        bundle_barcode: barcode,
      });

      const data = resp.data || {};
      const nextScanned = [...scannedBundles, {
        bundle_barcode: barcode,
        scanned_at: new Date().toISOString(),
        status: 'received',
      }];
      setScannedBundles(nextScanned);
      setBundleInput('');

      if (data.completed) {
        toast.success(`All ${data.received_count} bundles received — receipt completed`);
        clearDraft();
        resetToSelection();
      } else {
        toast.success(`Bundle ${barcode} received (${data.received_count}/${data.expected_count})`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive bundle');
      setBundleInput('');
    } finally {
      setIsScanning(false);
      // Defer the refocus: the <Input> is disabled={isScanning}, so calling
      // focus() synchronously (before React re-enables it) is a no-op. Running
      // it on the next tick lands focus back on the scan field after re-render.
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      received: { color: 'bg-green-100 text-green-800', label: 'Received' },
      accepted: { color: 'bg-blue-100 text-blue-800', label: 'Accepted' },
      shipped: { color: 'bg-orange-100 text-orange-800', label: 'Shipped' }
    };
    const cfg = config[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  const expectedTotal = expectedTotalOf(selectedDocument);
  const isComplete = expectedTotal > 0 && scannedBundles.length >= expectedTotal;
  const remaining = Math.max(expectedTotal - scannedBundles.length, 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backRoute)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Bundle Receipt Entry</h1>
              <p className="text-sm text-gray-400">{sectionDisplayName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchDocuments}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {resumedDraft && selectedDocument && (
          <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} className="mb-6" />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document Selection */}
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Select Document
              </CardTitle>
              <CardDescription>
                Choose a received document to start bundle receipt
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No documents ready for bundle receipt</p>
                  <p className="text-sm mt-2">Accept incoming consignments first</p>
                </div>
              ) : (
                <Select
                  value={selectedDocument?.transfer_id || selectedDocument?.entry_id || ''}
                  onValueChange={handleSelectDocument}
                >
                  <SelectTrigger data-testid="document-select">
                    <SelectValue placeholder="Select a document..." />
                  </SelectTrigger>
                  <SelectContent>
                    {documents.map((doc) => {
                      const docId = doc.transfer_id || doc.entry_id;
                      const bundleCount = expectedTotalOf(doc);
                      const recv = (doc.received_bundles || []).length;
                      const hasDraft = (drafts || []).some((d) => d.document_id === docId);
                      return (
                        <SelectItem key={docId} value={docId}>
                          {docId} - {doc.from_section || doc.vendor_name} ({recv > 0 ? `${recv}/${bundleCount}` : bundleCount} bundles)
                          {hasDraft && <span className="ml-2 font-semibold text-amber-600">· Draft</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}

              {selectedDocument && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{selectedDocument.transfer_id || selectedDocument.entry_id}</span>
                    {getStatusBadge(selectedDocument.status)}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>From: {selectedDocument.from_section || selectedDocument.vendor_name}</p>
                    <p>Bundles: {expectedTotal || 'N/A'}</p>
                  </div>
                </div>
              )}
              {selectedDocument && !selectedDocument.transfer_id && (
                <DeliveryPoSummary entryId={selectedDocument.entry_id} refreshKey={scannedBundles.length} className="mt-3" />
              )}
            </CardContent>
          </Card>

          {/* Bundle Scanning */}
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5" />
                Scan Bundles
              </CardTitle>
              <CardDescription>
                Each scan is saved instantly — the bundle is ready for the next operation right away
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedDocument ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>Select a document first</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-2">
                    <Input
                      ref={bundleInputRef}
                      value={bundleInput}
                      onChange={(e) => setBundleInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleScanBundle()}
                      placeholder="Scan or enter bundle barcode"
                      disabled={isScanning}
                      data-testid="bundle-input"
                    />
                    <Button
                      onClick={handleScanBundle}
                      disabled={isScanning || !bundleInput.trim()}
                      data-testid="scan-btn"
                    >
                      {isScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                    </Button>
                  </div>

                  <div
                    className="mb-4 flex items-center justify-between"
                    data-testid="scanned-bundle-count"
                  >
                    <span className="text-sm font-medium text-[#2D5F4F]">
                      {scannedBundles.length} bundle{scannedBundles.length !== 1 ? 's' : ''} received
                      {expectedTotal > 0 ? ` / ${expectedTotal} total` : ''}
                    </span>
                    {expectedTotal > 0 && (
                      <Badge
                        className={isComplete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}
                        data-testid="receipt-progress-text"
                      >
                        {isComplete ? 'Complete' : `Partially Received — ${remaining} remaining`}
                      </Badge>
                    )}
                  </div>

                  {scannedBundles.length > 0 && (
                    <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Bundle Barcode</TableHead>
                            <TableHead>Received At</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scannedBundles.map((bundle, idx) => (
                            <TableRow key={bundle.bundle_barcode} data-testid={`received-row-${bundle.bundle_barcode}`}>
                              <TableCell className="text-gray-500">{idx + 1}</TableCell>
                              <TableCell className="font-mono">{bundle.bundle_barcode}</TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {bundle.scanned_at ? new Date(bundle.scanned_at).toLocaleTimeString() : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>
                      Every scan is committed automatically. This document closes on its own once all
                      bundles are received.
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SectionBundleReceiptEntry;
